// Shared truck-speed pill.
// The backend sends no speed, so we derive it from the change in a truck's GPS
// position between map refreshes and render a small pill just above the truck.
// Stopped trucks (moved < 8 m since last fix) get NO pill — most of the fleet is
// parked most of the time and a wall of "Stopped" labels is noise. Moving trucks
// get a pill that is hidden by default and appears while the mouse hovers over
// hoverMarker (the truck marker). Returns the pill marker (also pushed into
// markersOut if provided) or undefined when nothing to draw.
//
// prevMap: a useRef(new Map()) held by the calling component, keyed by truck id,
// storing { lat, lng, t, text } of the previous fix so speed survives across refreshes.
//
// fixTime: the GPS fix timestamp (last_location.updated_at, stamped server-side on
// every driver POST). Speed must be measured between FIX times, not between map
// refreshes — drivers post every few minutes while the map refreshes every ~30s,
// so dividing a multi-minute position jump by a 30s refresh gap inflated speeds
// to 180+ mph. Without fixTime we fall back to the old refresh-clock estimate.
export function renderSpeedPill({ maps, map, prevMap, key, pos, deviceSpeed = null, fixTime = null, markersOut, zIndex = 1200, hoverMarker = null }) {
  if (!maps || !map || !pos) return;
  const cur = { lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) };
  if (isNaN(cur.lat) || isNaN(cur.lng)) return;

  const hav = (a, b) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const now = Date.now();
  // Fix timestamp (ms). NaN when the payload has no updated_at → legacy refresh-clock path.
  const ft = fixTime != null ? Date.parse(fixTime) : NaN;
  let text = null; // null on the first-ever fix (unknown) → no pill yet

  // A loaded dump truck tops out ~70-75 mph on the highway. Any computed/reported
  // speed above this is a bad GPS fix (position teleport between two scattered
  // fixes, or a spiky phone coords.speed) — NOT a real reading. Reject it and keep
  // the last good pill instead of flashing an impossible "105 mph".
  const MAX_PLAUSIBLE_MPH = 85;

  // Preferred path: device-reported speed (m/s from phone Geolocation coords.speed).
  // True instantaneous speed — no guessing. -1/NaN means "no fix" → fall through to delta.
  const ds = deviceSpeed != null ? Number(deviceSpeed) : NaN;
  if (!isNaN(ds) && ds >= 0) {
    const mph = ds * 2.23694;
    if (mph > MAX_PLAUSIBLE_MPH) {
      text = prevMap.get(key)?.text ?? null; // bad phone speed spike — keep last good
    } else {
      text = mph >= 2 ? `${Math.round(mph)} mph` : 'Stopped';
    }
    prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: isNaN(ft) ? now : ft, text });
  } else if (!isNaN(ft)) {
    // Fix-time path: measure distance over the interval between GPS fixes.
    const prev = prevMap.get(key);
    if (!prev) {
      prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: ft, text: null });
    } else if (ft <= prev.t) {
      // Same (or older) fix re-rendered on a map refresh — nothing new to measure.
      // Re-show the last computed pill instead of falsely reporting "Stopped".
      text = prev.text;
    } else {
      const moved = hav(prev, cur);
      const dtSec = Math.max((ft - prev.t) / 1000, 1);
      const mph = (moved / dtSec) * 2.23694;
      if (mph > MAX_PLAUSIBLE_MPH) {
        text = prev.text; // GPS teleport between bad fixes — keep last good
      } else {
        text = moved < 8 || mph < 2 ? 'Stopped' : `${Math.round(mph)} mph`;
      }
      prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: ft, text });
    }
  } else {
    // Legacy fallback (no fix timestamp): derive speed from position delta between refreshes.
    const prev = prevMap.get(key);
    if (prev) {
      const moved = hav(prev, cur);
      if (moved < 8) {
        text = 'Stopped';
        // Keep the anchor position but advance the clock, so when the truck DOES
        // move, mph is measured over the latest interval — not from an ancient fix
        // (which made dt huge and pinned every truck to "Stopped").
        prevMap.set(key, { lat: prev.lat, lng: prev.lng, t: now, text });
      } else {
        const dtSec = Math.max((now - prev.t) / 1000, 1);
        const mph = (moved / dtSec) * 2.23694;
        if (mph > MAX_PLAUSIBLE_MPH) {
          text = prev.text; // implausible — ignore this refresh-clock estimate
        } else {
          text = mph >= 2 ? `${Math.round(mph)} mph` : 'Stopped';
        }
        prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: now, text });
      }
    } else {
      prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: now, text: null });
    }
  }
  // Stopped (or unknown) trucks get no pill — no point flagging a parked fleet.
  if (!text || text === 'Stopped') return;

  const w = Math.max(54, text.length * 8 + 18);
  const svg = `
    <svg width="${w}" height="72" viewBox="0 0 ${w} 72" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" rx="9" ry="9" width="${w - 4}" height="20" fill="#16a34a" stroke="#ffffff" stroke-width="1.5"/>
      <text x="${w / 2}" y="16" text-anchor="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="11" font-weight="700">${text}</text>
    </svg>`;
  const marker = new maps.Marker({
    position: cur,
    map,
    icon: {
      url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
      scaledSize: new maps.Size(w, 72),
      anchor: new maps.Point(w / 2, 72), // bottom point → pill floats above the truck
    },
    zIndex,
    clickable: false,
    // Hover-only: hidden until the mouse is over the truck marker. Without a
    // hoverMarker to listen on there is nothing to hover, so show it always.
    visible: !hoverMarker,
  });
  if (hoverMarker && hoverMarker.addListener) {
    hoverMarker.addListener('mouseover', () => marker.setVisible(true));
    hoverMarker.addListener('mouseout', () => marker.setVisible(false));
  }
  if (markersOut && markersOut.push) markersOut.push(marker);
  return marker;
}
